<script setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';

import ContactListHeaderWrapper from 'dashboard/components-next/Contacts/ContactsHeader/ContactListHeaderWrapper.vue';
import ContactsActiveFiltersPreview from 'dashboard/components-next/Contacts/ContactsHeader/components/ContactsActiveFiltersPreview.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';

const props = defineProps({
  searchValue: { type: String, default: '' },
  headerTitle: { type: String, default: '' },
  showPaginationFooter: { type: Boolean, default: true },
  currentPage: { type: Number, default: 1 },
  totalItems: { type: Number, default: 100 },
  itemsPerPage: { type: Number, default: 15 },
  activeSort: { type: String, default: '' },
  activeOrdering: { type: String, default: '' },
  activeSegment: { type: Object, default: null },
  segmentsId: { type: [String, Number], default: 0 },
  hasAppliedFilters: { type: Boolean, default: false },
  isFetchingList: { type: Boolean, default: false },
});

const emit = defineEmits([
  'update:currentPage',
  'update:sort',
  'search',
  'applyFilter',
  'clearFilters',
]);

const route = useRoute();

const contactListHeaderWrapper = ref(null);

const isNotSegmentView = computed(() => {
  return route.name !== 'contacts_dashboard_segments_index';
});

const isActiveView = computed(() => {
  return route.name === 'contacts_dashboard_active';
});

const isLabelView = computed(
  () => route.name === 'contacts_dashboard_labels_index'
);

const showActiveFiltersPreview = computed(() => {
  return (
    (props.hasAppliedFilters || !isNotSegmentView.value) &&
    !props.isFetchingList &&
    !isLabelView.value &&
    !isActiveView.value
  );
});

const updateCurrentPage = page => {
  emit('update:currentPage', page);
};

const openFilter = () => {
  contactListHeaderWrapper.value?.onToggleFilters();
};

const handleSort = sortData => {
  emit('update:sort', sortData);
};
</script>

<template>
  <section class="contacts-layout">
    <div class="contacts-layout-inner">
      <ContactListHeaderWrapper
        ref="contactListHeaderWrapper"
        :show-search="isNotSegmentView && !isActiveView"
        :search-value="searchValue"
        :active-sort="activeSort"
        :active-ordering="activeOrdering"
        :header-title="headerTitle"
        :active-segment="activeSegment"
        :segments-id="segmentsId"
        :has-applied-filters="hasAppliedFilters"
        :is-label-view="isLabelView"
        :is-active-view="isActiveView"
        :total-contacts="totalItems"
        @update:sort="handleSort"
        @search="emit('search', $event)"
        @apply-filter="emit('applyFilter', $event)"
        @clear-filters="emit('clearFilters')"
      />

      <main class="contacts-main">
        <!-- Filter preview removed - now shown in header -->
        <slot
          name="default"
          :active-sort="activeSort"
          :active-ordering="activeOrdering"
          :on-sort="handleSort"
        />
      </main>

      <footer v-if="showPaginationFooter" class="contacts-footer">
        <PaginationFooter
          current-page-info="CONTACTS_LAYOUT.PAGINATION_FOOTER.SHOWING"
          :current-page="currentPage"
          :total-items="totalItems"
          :items-per-page="itemsPerPage"
          class="contacts-pagination-footer"
          @update:current-page="updateCurrentPage"
        />
      </footer>
    </div>
  </section>
</template>

<style scoped>
.contacts-layout {
  display: flex;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: #ffffff !important;
  color: #111827;

  /* Force contacts pages to always use light WLAB palette */
  --wlab-bg-primary: #ffffff;
  --wlab-bg-secondary: #f8fafc;
  --wlab-bg-tertiary: #f1f5f9;
  --wlab-bg-input: #ffffff;
  --wlab-bg-hover: #f1f5f9;
  --wlab-bg-card: #ffffff;
  --wlab-bg-sidebar: #f1f5f9;
  --wlab-bg-sidebar-inner: #ffffff;

  --wlab-text-primary: #0f172a;
  --wlab-text-secondary: #475569;
  --wlab-text-tertiary: #64748b;
  --wlab-text-muted: #94a3b8;
  --wlab-text-inverse: #ffffff;

  --wlab-border-default: #e2e8f0;
  --wlab-border-light: #f1f5f9;
  --wlab-border-focus: #0066ff;
}

.contacts-layout-inner {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
}

.contacts-main {
  flex: 1;
  overflow-y: auto;
}

.contacts-footer {
  position: sticky;
  bottom: 0;
  z-index: 10;
  padding: 12px 24px;
  background: #ffffff !important;
  border-top: 1px solid #e5e7eb !important;
}

/* Theme lock: keep Contacts white in dark mode too */
.contacts-layout :deep(.bg-n-background),
.contacts-layout :deep(.bg-n-solid-1),
.contacts-layout :deep(.dark\:bg-slate-900),
.contacts-layout :deep(.dark\:bg-slate-800) {
  background: #ffffff !important;
}

.contacts-layout :deep(.bg-n-solid-2) {
  background: #f8fafc !important;
}

.contacts-layout :deep(.bg-n-alpha-black2) {
  background: #ffffff !important;
}

.contacts-layout :deep(.bg-n-alpha-3) {
  background: #ffffff !important;
}

.contacts-layout :deep(.bg-n-alpha-2),
.contacts-layout :deep(.hover\:bg-n-alpha-2:hover) {
  background: #f1f5f9 !important;
}

.contacts-layout :deep(.border-n-weak),
.contacts-layout :deep(.border-n-strong) {
  border-color: #e5e7eb !important;
}

.contacts-layout :deep(.text-n-slate-12) {
  color: #111827 !important;
}

.contacts-layout :deep(.text-n-slate-11),
.contacts-layout :deep(.text-n-slate-10),
.contacts-layout :deep(.text-n-slate-9) {
  color: #64748b !important;
}

.contacts-pagination-footer {
  background: #ffffff !important;
  outline-color: #e5e7eb !important;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06) !important;
}

.contacts-pagination-footer::before {
  content: none !important;
}
</style>
